import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Loader2, Info, PlusCircle, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const REASON_CODES = [
  { value: 'initial_classification_correction', label: 'Initial classification correction' },
  { value: 'taxonomy_update', label: 'Taxonomy update' },
  { value: 'better_fit_discovered', label: 'Better fit discovered' },
  { value: 'data_correction', label: 'Data correction' },
  { value: 'other', label: 'Other (specify in comments)' },
];

const EditPartModal = ({ isOpen, onClose, part, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    description: '',
    category: '',
    uom: 'EA',
    status: 'active',
  });
  
  // Classification
  const [mainGroups, setMainGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [selectedMainGroup, setSelectedMainGroup] = useState('');
  const [selectedSubgroup, setSelectedSubgroup] = useState('');
  const [customSubgroupName, setCustomSubgroupName] = useState('');
  const [willCreateSubgroup, setWillCreateSubgroup] = useState(false);
  
  // Audit
  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');
  
  // Track original values for comparison
  const [originalValues, setOriginalValues] = useState({});
  
  // Initialize form when part changes
  useEffect(() => {
    if (isOpen && part) {
      const initial = {
        description: part.description || '',
        category: part.category || '',
        uom: part.uom || 'EA',
        status: part.status || 'active',
      };
      setFormData(initial);
      setSelectedMainGroup(part.main_group_code || '');
      setSelectedSubgroup(part.subgroup_code || '');
      setOriginalValues({
        ...initial,
        main_group_code: part.main_group_code,
        subgroup_code: part.subgroup_code,
      });
      setReasonCode('');
      setComment('');
      setWillCreateSubgroup(false);
      setCustomSubgroupName('');
      loadMainGroups();
    }
  }, [isOpen, part]);
  
  // Load subgroups when main group changes
  useEffect(() => {
    if (selectedMainGroup) {
      loadSubgroups(selectedMainGroup);
    } else {
      setSubgroups([]);
    }
  }, [selectedMainGroup]);
  
  // Check for changes
  useEffect(() => {
    const changed = 
      formData.description !== originalValues.description ||
      formData.category !== originalValues.category ||
      formData.uom !== originalValues.uom ||
      formData.status !== originalValues.status ||
      selectedMainGroup !== originalValues.main_group_code ||
      selectedSubgroup !== originalValues.subgroup_code;
    
    setHasChanges(changed);
  }, [formData, selectedMainGroup, selectedSubgroup, originalValues]);
  
  // Check if subgroup needs to be created
  useEffect(() => {
    if (selectedMainGroup && selectedSubgroup && subgroups.length > 0) {
      const exists = subgroups.some(sg => sg.subgroup_code === selectedSubgroup);
      setWillCreateSubgroup(!exists && selectedSubgroup !== originalValues.subgroup_code);
    } else {
      setWillCreateSubgroup(false);
    }
  }, [selectedMainGroup, selectedSubgroup, subgroups, originalValues.subgroup_code]);
  
  const loadMainGroups = async () => {
    try {
      const response = await api.get('/api/v1/taxonomy/main-groups');
      setMainGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load main groups:', error);
    }
  };
  
  const loadSubgroups = async (mainGroupCode) => {
    try {
      const response = await api.get(`/api/v1/taxonomy/subgroups/${mainGroupCode}`);
      setSubgroups(response.data || []);
    } catch (error) {
      console.error('Failed to load subgroups:', error);
      setSubgroups([]);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleMainGroupChange = (code) => {
    setSelectedMainGroup(code);
    setSelectedSubgroup('');
    setWillCreateSubgroup(false);
    setCustomSubgroupName('');
  };
  
  const handleSubgroupChange = (code) => {
    if (code === '__new__') {
      setSelectedSubgroup('');
      setWillCreateSubgroup(true);
    } else {
      setSelectedSubgroup(code);
    }
  };
  
  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  };
  
  const isClassificationChanged = () => {
    return selectedMainGroup !== originalValues.main_group_code ||
           selectedSubgroup !== originalValues.subgroup_code;
  };
  
  const handleSubmit = async () => {
    // Validation
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    
    if (!selectedMainGroup) {
      toast.error('Please select a main group');
      return;
    }
    
    if (!selectedSubgroup) {
      toast.error('Please select or enter a subgroup');
      return;
    }
    
    // Require reason code for classification changes
    if (isClassificationChanged() && !reasonCode) {
      toast.error('Please select a reason for the classification change');
      return;
    }
    
    // Require comment if reason is "other"
    if (reasonCode === 'other' && !comment.trim()) {
      toast.error('Please provide a comment explaining the change');
      return;
    }
    
    // Require subgroup name for new subgroups
    if (willCreateSubgroup && !customSubgroupName.trim()) {
      toast.error('Please provide a name for the new subgroup');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get subgroup name - either from custom input or lookup from subgroups list
      let subgroupName = customSubgroupName;
      if (!willCreateSubgroup && selectedSubgroup) {
        const sg = subgroups.find(s => s.subgroup_code === selectedSubgroup);
        subgroupName = sg ? sg.name : selectedSubgroup;
      }
      
      const updateData = {
        description: formData.description,
        category: formData.category || null,
        uom: formData.uom,
        status: formData.status,
        main_group_code: selectedMainGroup,
        // Backend expects subgroup_name, not subgroup_code
        subgroup_name: subgroupName,
      };
      
      // Add reason code if classification changed
      if (isClassificationChanged()) {
        updateData.reason_code = reasonCode;
        updateData.comment = comment || null;
      }
      
      const response = await api.put(`/api/v1/parts/by-hpn/${part.hpn_compact}`, updateData);
      
      // Show success message
      if (response.data.subgroup_created) {
        toast.success(
          <div>
            <p className="font-semibold">Part updated successfully!</p>
            <p className="text-sm text-green-600 mt-1 flex items-center">
              <PlusCircle className="w-3 h-3 mr-1" />
              New subgroup &quot;{customSubgroupName}&quot; was created
            </p>
          </div>
        );
      } else {
        toast.success('Part updated successfully!');
      }
      
      onSuccess?.(response.data);
      onClose();
      
    } catch (error) {
      console.error('Failed to update part:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update part';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen || !part) return null;
  
  const getMainGroupName = (code) => {
    const group = mainGroups.find(g => g.code === code);
    return group ? group.name : code;
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-haden-dark text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Edit3 className="w-5 h-5" />
            <div>
              <h2 className="text-lg font-semibold">Edit Part</h2>
              <p className="text-sm text-slate-300 font-mono">{part.hpn_display}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="close-edit-modal-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-6">
            {/* Immutable HPN Notice */}
            <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">HPN is permanent</p>
                <p className="text-blue-600">The Haden Part Number cannot be changed once assigned.</p>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent resize-none"
                data-testid="edit-description"
              />
            </div>
            
            {/* Classification Section */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center">
                <span>Classification</span>
                {isClassificationChanged() && (
                  <span className="ml-2 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                    Modified
                  </span>
                )}
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Main Group */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Main Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedMainGroup}
                    onChange={(e) => handleMainGroupChange(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                    data-testid="edit-main-group"
                  >
                    <option value="">Select...</option>
                    {mainGroups.map((group) => (
                      <option key={group.code} value={group.code}>
                        {group.code} - {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Subgroup */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Subgroup <span className="text-red-500">*</span>
                  </label>
                  {subgroups.length > 0 ? (
                    <select
                      value={selectedSubgroup}
                      onChange={(e) => handleSubgroupChange(e.target.value)}
                      disabled={!selectedMainGroup}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent disabled:bg-slate-100"
                      data-testid="edit-subgroup"
                    >
                      <option value="">Select...</option>
                      {subgroups.map((sg) => (
                        <option key={sg.subgroup_code} value={sg.subgroup_code}>
                          {sg.subgroup_code} - {sg.name}
                        </option>
                      ))}
                      <option value="__new__">+ Create new subgroup</option>
                    </select>
                  ) : selectedMainGroup ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={selectedSubgroup}
                        onChange={(e) => setSelectedSubgroup(e.target.value.toUpperCase())}
                        placeholder="Enter code (e.g., 1A)"
                        maxLength={2}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent font-mono uppercase"
                        data-testid="edit-subgroup-code-input"
                      />
                      <p className="text-xs text-amber-600">
                        No subgroups exist. Enter a new code.
                      </p>
                    </div>
                  ) : (
                    <select disabled className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-slate-100">
                      <option>Select main group first</option>
                    </select>
                  )}
                </div>
              </div>
              
              {/* New Subgroup Input */}
              {willCreateSubgroup && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-green-700 mb-3">
                    <PlusCircle className="w-4 h-4" />
                    <p className="text-sm font-medium">New subgroup will be created</p>
                  </div>
                  
                  {!selectedSubgroup && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Subgroup Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={selectedSubgroup}
                        onChange={(e) => setSelectedSubgroup(e.target.value.toUpperCase())}
                        placeholder="e.g., 1A, 2B"
                        maxLength={2}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono uppercase"
                        data-testid="edit-new-subgroup-code"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Subgroup Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={customSubgroupName}
                      onChange={(e) => setCustomSubgroupName(e.target.value)}
                      placeholder="e.g., Industrial Relays"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="edit-subgroup-name"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Additional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Scroll Compressors"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                  data-testid="edit-category"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unit of Measure
                </label>
                <select
                  name="uom"
                  value={formData.uom}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                  data-testid="edit-uom"
                >
                  <option value="EA">EA - Each</option>
                  <option value="M">M - Meter</option>
                  <option value="FT">FT - Feet</option>
                  <option value="KG">KG - Kilogram</option>
                  <option value="L">L - Liter</option>
                  <option value="SET">SET - Set</option>
                  <option value="BOX">BOX - Box</option>
                </select>
              </div>
            </div>
            
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                data-testid="edit-status"
              >
                <option value="active">Active</option>
                <option value="obsolete">Obsolete</option>
              </select>
            </div>
            
            {/* Reason for Change (shown when classification changed) */}
            {isClassificationChanged() && (
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-center space-x-2 mb-4">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <h3 className="text-sm font-semibold text-slate-900">
                    Reason for Classification Change
                  </h3>
                </div>
                
                <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Reason Code <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={reasonCode}
                      onChange={(e) => setReasonCode(e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent"
                      data-testid="edit-reason-code"
                    >
                      <option value="">Select a reason...</option>
                      {REASON_CODES.map((rc) => (
                        <option key={rc.value} value={rc.value}>
                          {rc.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Comment {reasonCode === 'other' && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={reasonCode === 'other' ? 'Please explain the reason for this change...' : 'Additional notes (optional)'}
                      rows={2}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-haden-accent focus:border-transparent resize-none"
                      data-testid="edit-comment"
                    />
                  </div>
                  
                  <div className="text-sm text-amber-700">
                    <p>Classification change will be recorded in the audit trail:</p>
                    <p className="font-mono mt-1">
                      {originalValues.main_group_code}:{originalValues.subgroup_code} → {selectedMainGroup}:{selectedSubgroup}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {hasChanges ? (
              <span className="text-amber-600">You have unsaved changes</span>
            ) : (
              <span>No changes made</span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-slate-600 hover:text-slate-900"
              data-testid="edit-cancel-button"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !hasChanges}
              className="flex items-center space-x-2 px-6 py-2 bg-haden-accent text-white rounded-lg hover:bg-haden-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="edit-save-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPartModal;
